// views/Profile/AccountSettings.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import GroupWorkRoundedIcon from "@mui/icons-material/GroupWorkRounded";
import PreferencesModal from "../Login/components/PreferencesModal";
import DeleteAccount from "./DeleteAccount";
import Loading from "../../components/Loading/Loading";
import { useAuthToken } from "../../hooks/UseAuthToken";
import { useFetchColleges } from "../../hooks/FetchColleges";
import { useBasicUserData } from "../../hooks/UserData";

const SERVER_BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

const AccountSettings = ({ onLogout }) => {
  const token = useAuthToken(); // used only for saving
  const [basicUser, basicLoading, basicError] = useBasicUserData();
  const [
    supportedColleges,
    supportedCollegesLoading,
    supportedCollegesError,
  ] = useFetchColleges();

  const [apiError, setApiError] = useState(null);

  const [mode, setMode] = useState("");
  const [selectedCollegeIds, setSelectedCollegeIds] = useState([]);
  const [showPrefsModal, setShowPrefsModal] = useState(false);
  const [ownCollegeId, setOwnCollegeId] = useState(null);

  // useFetchColleges already hit /colleges; just alias it
  const colleges = supportedColleges || [];

  // 1) Derive prefs from basicUser once it’s available
  useEffect(() => {
    if (!basicUser) return;

    const prefs = basicUser.matchingPreferences || {};
    const currentMode = prefs.mode || "";
    const currentCollegeIds = (prefs.collegeIds || []).map((id) =>
      typeof id === "string" ? id : id.toString()
    );

    setOwnCollegeId(basicUser.collegeId || null);
    setMode(currentMode);
    setSelectedCollegeIds(currentCollegeIds);
  }, [basicUser]);

  const handleSavePreferences = useCallback(
    async ({ mode: newMode, collegeIds }) => {
      if (!token) {
        alert("You’re not authenticated. Please log in again.");
        return;
      }

      try {
        await axios.put(
          `${SERVER_BASE_URL}/user/matching-preferences`,
          { mode: newMode, collegeIds },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setMode(newMode);
        setSelectedCollegeIds(
          newMode === "SELECTED_COLLEGES" ? collegeIds : []
        );
        setShowPrefsModal(false);
        setApiError(null);
      } catch (err) {
        console.error("Error saving preferences from AccountSettings:", err);
        if (err.response?.data?.message) {
          alert(err.response.data.message);
        } else {
          alert("Failed to save. Please try again.");
        }
      }
    },
    [token]
  );

  const ownCollegeName = useMemo(
    () => colleges.find((c) => c.id === ownCollegeId)?.name,
    [colleges, ownCollegeId]
  );

  const renderModeLabel = () => {
    if (mode === "OWN_COLLEGE")
      return ownCollegeName ? `Only ${ownCollegeName}` : "Only my school";
    if (mode === "SELECTED_COLLEGES") return "Specific schools";
    return "Anyone on VibeU";
  };

  const modeMeta = useMemo(() => {
    if (mode === "OWN_COLLEGE") {
      return {
        icon: <SchoolRoundedIcon sx={{ fontSize: 18 }} />,
        title: "Campus-only",
        subtitle: "Stay within your school’s vibe.",
      };
    }
    if (mode === "SELECTED_COLLEGES") {
      return {
        icon: <GroupWorkRoundedIcon sx={{ fontSize: 18 }} />,
        title: "Handpicked schools",
        subtitle: "Match across a curated list.",
      };
    }
    return {
      icon: <PublicRoundedIcon sx={{ fontSize: 18 }} />,
      title: "Open matching",
      subtitle: "Meet anyone using VibeU.",
    };
  }, [mode]);

  const selectedColleges = useMemo(() => {
    if (mode !== "SELECTED_COLLEGES" || selectedCollegeIds.length === 0)
      return [];
    return colleges.filter((c) => selectedCollegeIds.includes(c.id));
  }, [mode, selectedCollegeIds, colleges]);

  const isLoading = basicLoading || supportedCollegesLoading;
  const anyError = apiError || basicError || supportedCollegesError;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Loading />
      </Box>
    );
  }

  if (anyError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" sx={{ mb: 1 }}>
          Couldn’t load settings. Please try refreshing.
        </Typography>
        {basicError && (
          <Typography variant="caption" color="error">
            (User error: {basicError.message})
          </Typography>
        )}
        {supportedCollegesError && (
          <Typography variant="caption" color="error" display="block">
            (Colleges error: {supportedCollegesError.message})
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Simple header */}
      <Box sx={{ px: 0.5 }}>
        <Typography
          sx={{
            fontWeight: 800,
            color: "white",
            fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.35rem" },
            letterSpacing: "0.01em",
          }}
        >
          Settings
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            color: "rgba(255,255,255,0.7)",
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
          }}
        >
          Quick controls for how you match.
        </Typography>
      </Box>

      {/* Matching preferences (modern glass card) */}
      <Card
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.10)",
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.92) 0%, rgba(11,18,32,0.88) 100%)",
          backdropFilter: "blur(10px)",
          color: "#e5e7eb",
          boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                background: "rgba(59,130,246,0.16)",
                border: "1px solid rgba(59,130,246,0.35)",
                color: "#bfdbfe",
                flexShrink: 0,
              }}
            >
              <TuneRoundedIcon />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "white",
                  fontSize: { xs: "0.98rem", sm: "1.05rem" },
                }}
              >
                Matching
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  color: "rgba(255,255,255,0.68)",
                  fontSize: { xs: "0.8rem", sm: "0.88rem" },
                }}
              >
                Choose who you get paired with.
              </Typography>
            </Box>

            <Button
              onClick={() => setShowPrefsModal(true)}
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: "999px",
                fontWeight: 700,
                px: 2,
                background:
                  "linear-gradient(90deg, rgba(59,130,246,1) 0%, rgba(37,99,235,1) 100%)",
                boxShadow: "0 10px 22px rgba(37,99,235,0.25)",
                "&:hover": { filter: "brightness(1.05)" },
              }}
            >
              Edit
            </Button>
          </Box>

          <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.10)" }} />

          {/* Current mode row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                {modeMeta.icon}
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "white",
                    fontSize: { xs: "0.9rem", sm: "0.95rem" },
                  }}
                >
                  {modeMeta.title}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.65)",
                    fontSize: { xs: "0.75rem", sm: "0.82rem" },
                  }}
                >
                  {modeMeta.subtitle}
                </Typography>
              </Box>
            </Box>

            <Chip
              label={renderModeLabel()}
              size="small"
              sx={{
                borderRadius: "999px",
                backgroundColor: "rgba(191,219,254,0.90)",
                color: "#0b1220",
                fontWeight: 800,
                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                maxWidth: "100%",
              }}
            />
          </Box>

          {/* Selected schools */}
          {mode === "SELECTED_COLLEGES" && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "white",
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                }}
              >
                Selected schools
              </Typography>

              {selectedColleges.length === 0 ? (
                <Typography
                  sx={{
                    mt: 0.75,
                    color: "rgba(255,255,255,0.6)",
                    fontSize: { xs: "0.78rem", sm: "0.85rem" },
                  }}
                >
                  None selected yet — tap <b>Edit</b> to add some.
                </Typography>
              ) : (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={0.75}
                  sx={{ mt: 1 }}
                >
                  {selectedColleges.map((c) => (
                    <Chip
                      key={c.id}
                      label={c.name}
                      size="small"
                      sx={{
                        borderRadius: "999px",
                        fontSize: { xs: "0.72rem", sm: "0.78rem" },
                        backgroundColor: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "rgba(255,255,255,0.9)",
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete account */}
      <DeleteAccount onLogout={onLogout} />

      {/* Re-use onboarding preferences modal */}
      <PreferencesModal
        open={showPrefsModal}
        colleges={colleges}
        initialMode={mode}
        initialSelectedCollegeIds={selectedCollegeIds}
        onSave={handleSavePreferences}
        ownCollegeName={ownCollegeName}
        showCloseButton={true}
        onClose={() => setShowPrefsModal(false)}
      />
    </Box>
  );
};

AccountSettings.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

export default AccountSettings;
